import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@vr/lib';
import moment from 'moment';
import devices from '../styles/devices';
import dashboardColors from '../helpers/dashboardColors';
import Button from '../elements/Button';
import Icon from '../elements/Icon';

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;
const Details = styled.div`
  display: flex;
  margin-bottom: 25px;

  @media (max-width: ${devices.tablet}) {
    justify-content: center;
  }
`;
const Detail = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 30px;
  color: ${({ theme }) => theme.colors.gray400};
  text-align: left;

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: 0;
  }

  @media (max-width: ${devices.tablet}) {
    text-align: center;
  }

  @media (max-width: 375px) {
    margin: 0 15px;
  }
`;
const Name = styled.p`
  font-size: 0.75rem;
  margin-bottom: 4px;
`;
const Value = styled.p`
  font-size: 0.9375rem;
  font-weight: 600;
`;
const Actions = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: 2px;

  & > div {
    border: 2px solid
      ${({ theme, type }) => dashboardColors(theme, type).color3};
    color: ${({ theme, type }) => dashboardColors(theme, type).color3};
    margin: 0 3.5px;
    &:first-of-type {
      margin-left: 0;
    }

    &:last-of-type {
      margin-right: 0;
    }
  }
`;
const Action = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 107px;
  height: 85px;
  border-radius: 6px;
  background: transparent;
  padding: 10px 12px;
  font-weight: 700;
  cursor: pointer;
`;
const CustomIcon = styled(FontAwesomeIcon)`
  width: 16px;
  height: 16px;
  font-weight: 500;
`;
const Text = styled.p`
  font-size: 0.75rem;
  @media (max-width: 374px) {
    display: ${({ hideXS }) => hideXS && 'none'};
  }
  @media (min-width: 375px) {
    display: ${({ hideSM }) => hideSM && 'none'};
  }
`;
const ToConfirmContainer = styled.div`
  display: flex;
  align-items: center;
`;
const QuestionIcon = styled(FontAwesomeIcon)`
  font-size: 0.8125rem;
  color: green;
  margin-left: 7px;
  cursor: pointer;
`;

const UnblockButton = styled(Button)`
  width: 100%;
  max-width: 333px;
  margin: 25px 0 0 !important;
  @media (max-width: ${devices.tablet}) {
    margin-top: 0 !important;
  }
`;
const BlockedMsg = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.gray400};
  margin-top: 25px;
  text-align: center;
`;

function benefitToConfirm(onClick) {
  return (
    <ToConfirmContainer>
      <Value>a confirmar</Value>
      <QuestionIcon
        onClick={() => onClick('to_confirm')}
        icon={['fas', 'question-circle']}
      />
    </ToConfirmContainer>
  );
}

export default function CardActions({ onClick, card = {} }) {
  const {
    codigoProduto: type,
    proximaDataCredito: dateNextCredit,
    gastoDiarioMedio: dailySpendOver,
    statusCartao: status,
    valorProximoBeneficio,
    ar,
    tipo,
  } = card;

  return (
    <Container>
      {status === 3 && (
        <>
          <Details>
            <Detail>
              <Name>Próximo benefício</Name>
              {dateNextCredit ? (
                <Value>
                  {moment(dateNextCredit, 'DD/MM/YYYY').format('DD/MM')} &ensp;
                  {valorProximoBeneficio?.toBRL()}
                </Value>
              ) : (
                benefitToConfirm(onClick)
              )}
            </Detail>

            {dailySpendOver > 0 && (
              <Detail>
                <Name>Gasto diário Sugerido</Name>
                <Value>{dailySpendOver?.toBRL()}</Value>
              </Detail>
            )}
          </Details>
          <Actions type={tipo ? 'caixa' : type}>
            <Action
              className="ondeusar-step"
              onClick={() => onClick('where_to_use')}
            >
              <CustomIcon icon="map-marker" />
              <Text hideXS>Onde usar</Text>
              <Text hideXS>meu cartão</Text>
              <Text hideSM>Onde</Text>
              <Text hideSM>usar</Text>
            </Action>
            <Action
              data-testid="change-card-password"
              className="alterarsenha-step"
              onClick={() => onClick('change_password')}
            >
              <CustomIcon icon="key" />
              <Text>Alterar</Text>
              <Text>senha</Text>
            </Action>
            <Action
              className="bloquear-step"
              onClick={() => onClick('lock_card')}
            >
              <CustomIcon icon="lock" />
              <Text>Bloquear</Text>
              <Text>cartão</Text>
            </Action>
          </Actions>
        </>
      )}
      {status === 2 && (
        <>
          <UnblockButton
            variant="outlined"
            color={type}
            label="Desbloquear novo cartão"
            startIcon={<Icon icon={['far', 'unlock']} />}
            disabled={ar}
            onClick={() => onClick('unlock_card')}
          />
          {ar && (
            <BlockedMsg>
              O desbloqueio do seu cartão ainda não foi autorizado por sua
              empresa. Em caso de dúvidas, entre em contato com seu RH.
            </BlockedMsg>
          )}
        </>
      )}
    </Container>
  );
}

CardActions.propTypes = {
  card: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};
